import { z } from 'zod'
import type {
  RewardConditionGroupProgressData,
  RewardConditionProgressData,
  UserRewardAcquisitionStatus,
} from './user'
import { DateTimeStringSchema } from './common'
import { CouponUsageStateSchema } from './coupon'

export type { RewardConditionProgressData, RewardConditionGroupProgressData }

// ---- 賞品グループ ----
export const RewardGroupDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.enum(['ALL', 'SELECT_ONE']),
})

// ---- 応募フォームフィールド定義（動的構造） ----
/** 応募フォームフィールド定義（管理画面で設定された動的フィールド） */
export const ApplicationFormFieldDataSchema = z.object({
  id: z.number(),
  fieldKey: z.string(),
  label: z.string(),
  /** text / email / tel / textarea / select / checkbox */
  inputType: z.enum(['text', 'email', 'tel', 'textarea', 'select', 'checkbox']),
  isRequired: z.boolean(),
  sortOrder: z.number(),
  options: z.array(z.string()).nullable().optional().default(null),
})

export type ApplicationFormFieldData = z.infer<typeof ApplicationFormFieldDataSchema>

// ---- 応募フォーム定義 ----
/** 応募フォーム定義（管理画面で構成されるフィールド一覧） */
export const ApplicationFormDataSchema = z.object({
  /** フィールド定義のソース: "prize" | "campaign" */
  source: z.enum(['prize', 'campaign']),
  fields: z.array(ApplicationFormFieldDataSchema),
  /** 同一キャンペーン内の直近入力値マップ。プリフィル表示に使用する。checkbox フィールドは string[]、その他は string */
  // バックエンドが prefill なし時に [] を返す場合があるため、配列は null に変換して受け入れる
  prefill: z.preprocess(
    (v) => (Array.isArray(v) ? null : v),
    z.record(z.string(), z.union([z.string(), z.array(z.string())])).nullable().optional().default(null),
  ),
})

// ---- ギフトコード情報 ----
export const GiftCodeDataSchema = z.object({
  code: z.string(),
  url: z.string().nullable(),
  /** NONE / CODE / URL / QR / CODE_URL / QR_URL / PAYPAY_POINT */
  codeDisplayType: z.enum(['NONE', 'CODE', 'URL', 'QR', 'CODE_URL', 'QR_URL', 'PAYPAY_POINT']),
  startAt: z.string().nullable().optional().default(null),
  endAt: z.string().nullable().optional().default(null),
  contactNumber: z.string().nullable().optional().default(null),
  amount: z.number().int().nullable().optional().default(null),
})

// ---- 賞品情報 ----
export const PrizeDataSchema = z.object({
  /** ハズレ時は null */
  id: z.number().nullable(),
  /** ハズレ時は null */
  name: z.string().nullable(),
  /** ITEM / DLC / AR_PHOTO / COUPON / GIFT_CODE / GIFTEE。ハズレ時は null */
  type: z.enum(['ITEM', 'DLC', 'AR_PHOTO', 'COUPON', 'GIFT_CODE', 'GIFTEE']).nullable(),
  /** NONE / CLERK_REDEMPTION / APPLICATION_FORM。ハズレ時は null */
  fulfillmentType: z.enum(['NONE', 'CLERK_REDEMPTION', 'APPLICATION_FORM']).nullable(),
  /** DLC コンテンツURL（type=DLC のみ）。それ以外の type ではフィールド自体が省略される場合がある */
  contentFile: z.string().nullable().optional().default(null),
  /** ギフトコード情報（type=GIFT_CODE のみ）。それ以外の type ではフィールド自体が省略される場合がある */
  code: GiftCodeDataSchema.nullable().optional().default(null),
  /** 応募フォーム定義（fulfillmentType=APPLICATION_FORM かつ status=ACHIEVED の場合のみ非 null） */
  applicationForm: ApplicationFormDataSchema.nullable().optional().default(null),
  /** スポット指定クーポンか否か（true の場合、couponSpots からスポット選択が必要） */
  couponSpecificSpots: z.boolean().nullable().optional(),
  /** 利用可能スポット一覧（couponSpecificSpots=true のみ） */
  couponSpots: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
      }),
    )
    .nullable()
    .optional(),
  /** クーポン画像URL（type=COUPON のみ） */
  image: z.string().nullable().optional(),
  /** COUPONタイプ時のみ。expiryType=RELATIVE の場合の利用可能日数 */
  validDays: z.number().nullable().optional(),
  /** COUPONタイプ時のみ。FIXED は配布開始日時、RELATIVE は獲得日時からの利用開始日時（ISO 8601） */
  startAt: DateTimeStringSchema.nullable().optional(),
  /** COUPONタイプ時のみ。FIXED は配布終了日時、RELATIVE は獲得日時からの利用終了日時（ISO 8601） */
  endAt: DateTimeStringSchema.nullable().optional(),
  /**
   * POST /api/v2/rewards/{id} レスポンスに含まれる抽選ハズレ判定フラグ。
   * true の場合、この賞品エントリはハズレを表す（id/name/type/fulfillmentType が null）。
   * PR #2609 以降: ルートの isLotteryLost が除去されこちらに移動。
   * API doc: boolean, nullable: true（AcquireReward エンドポイントのみ設定）
   */
  isLotteryLost: z.boolean().nullable().optional(),
  /**
   * POST /api/v2/rewards/{id} レスポンスに含まれるトランザクションID。
   * 応募フォーム送信先の {id} として使用する。
   * PR #2609 以降: ルートの userRewardTransactionId が除去されこちらに移動。
   */
  userRewardTransactionId: z.number().nullable().optional(),
  /** COUPONタイプ時のみ。CouponConfig.id（POST /api/v2/coupons/{id} で使用） */
  couponConfigId: z.number().nullable().optional(),
  /** COUPONタイプ時のみ。CouponConfig.name */
  couponConfigName: z.string().nullable().optional(),
  /**
   * COUPONタイプ時のみ。利用制限・利用状況（GET /api/v2/user/rewards/{id} の当選エントリでのみ非 null）。
   * 一覧 API（GET /api/v2/user/rewards）や POST /api/v2/rewards/{id} のレスポンスには含まれないため
   * フィールド欠落を許容する（他のクーポン系フィールドと同じく .default() は付けない）。
   */
  couponUsage: CouponUsageStateSchema.nullable().optional(),
}).superRefine((data, ctx) => {
  if (data.couponSpecificSpots === true) {
    if (!Array.isArray(data.couponSpots) || data.couponSpots.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['couponSpots'],
        message: 'couponSpecificSpots=true のとき couponSpots は非空配列が必要です',
      })
    }
    if (data.couponConfigId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['couponConfigId'],
        message: 'couponSpecificSpots=true のとき couponConfigId は必須です',
      })
    }
  }
})
export const UserRewardTransactionDataSchema = z.object({
  /** UserRewardTransaction.id（レガシー、reward.id が入っている場合あり） */
  id: z.number(),
  /** UserRewardTransaction.transactionId（exchange エンドポイントで使用） */
  transactionId: z.number().nullable().optional(),
  /** ACHIEVED / EXCHANGED / USED / UNAPPLIED / APPLIED / LOST */
  status: z.enum(['ACHIEVED', 'EXCHANGED', 'USED', 'UNAPPLIED', 'APPLIED', 'LOST']),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  name: z.string().nullable(),
  /** NONE / CLERK_REDEMPTION / APPLICATION_FORM */
  fulfillmentType: z.string().nullable(),
  /** INFINITE / FINITE */
  inventoryType: z.string().nullable(),
  stock: z.number().nullable(),
  rewardGroup: RewardGroupDataSchema.nullable(),
  prize: PrizeDataSchema.nullable(),
  /** Giftee Box 発行URL（prize.type に GIFTEE がある場合） */
  gifteeBoxUrl: z.string().nullable(),
})

// ---- 特典獲得レスポンス（POST /api/v2/rewards/{id}） ----
export const UserRewardTransactionGetResponseSchema = z.object({
  /**
   * 通常（新フォーマット）: UserRewardTransactionData オブジェクトが返る。
   * 旧フォーマット互換: reward が null の場合、ルートレベルの isLotteryLost / userRewardTransactionId を参照する。
   * ハズレ判定の優先順位: reward.status === 'LOST' → reward.prize.isLotteryLost → ルートの isLotteryLost
   */
  reward: UserRewardTransactionDataSchema.nullable(),
  /** 旧フォーマット互換: ルートレベルのハズレフラグ（reward が null の場合に使用） */
  isLotteryLost: z.boolean().nullable().optional(),
  /** 旧フォーマット互換: ルートレベルのトランザクションID（reward が null の場合に使用） */
  userRewardTransactionId: z.number().nullable().optional(),
})

/** 獲得試行エントリ（当選・ハズレ両方を含む） */
export const PrizeHistoryDataSchema = z
  .object({
    /** 賞品情報（ハズレの場合 null） */
    prize: PrizeDataSchema.nullable(),
    /** 獲得試行 TX の ID（user_reward_transactions.id）。応募フォーム送信の {id} として使用する */
    userRewardTransactionId: z.number(),
    /** 獲得試行日時 */
    acquiredAt: z.string().nullable(),
    /** この試行が抽選ハズレだったかどうか */
    isLotteryLost: z.boolean(),
    /** 獲得試行のステータス（ACHIEVED / EXCHANGED / USED / UNAPPLIED / APPLIED / LOST） */
    status: z.enum(['ACHIEVED', 'EXCHANGED', 'USED', 'UNAPPLIED', 'APPLIED', 'LOST']).optional(),
  })
  .superRefine((data, ctx) => {
    // 当選エントリ（isLotteryLost=false）は prize / type / fulfillmentType が必須
    if (!data.isLotteryLost) {
      if (data.prize === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '当選エントリの prize は null であってはなりません',
          path: ['prize'],
        })
        return
      }
      if (data.prize.type === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '当選エントリの prize.type は null であってはなりません',
          path: ['prize', 'type'],
        })
      }
      if (data.prize.fulfillmentType === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '当選エントリの prize.fulfillmentType は null であってはなりません',
          path: ['prize', 'fulfillmentType'],
        })
      }
    }
  })

export type PrizeHistoryData = z.infer<typeof PrizeHistoryDataSchema>

export type RewardGroupData = z.infer<typeof RewardGroupDataSchema>
export type ApplicationFormData = z.infer<typeof ApplicationFormDataSchema>
export type GiftCodeData = z.infer<typeof GiftCodeDataSchema>
export type PrizeData = z.infer<typeof PrizeDataSchema>
export type UserRewardTransactionData = z.infer<typeof UserRewardTransactionDataSchema>
export type UserRewardTransactionGetResponse = z.infer<typeof UserRewardTransactionGetResponseSchema>

/** POST /api/v2/user/reward-transactions/{id}/application-form リクエストボディ型 */
export type ApplicationFormSubmitRequest = {
  fields: { fieldKey: string; value: string | string[] }[]
}

export const StampInRewardDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.string().nullable(),
})

export const RewardDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  status: z.string(),
  image: z.string().nullable(),
  url: z.string().nullable(),
  label: z.string().nullable(),
  /** 特典の説明文（HTML文字列）。未設定時は null */
  description: z.string().nullable().optional().default(null),
  groupId: z.number().nullable(),
  groupName: z.string().nullable(),
  groupType: z.enum(['NONE', 'SINGLE', 'MULTI']),
  stamps: z.array(StampInRewardDataSchema).optional().default([]),
  /** スタンプ消費が必要な特典か。バックエンド実装済み（Issue #2783） */
  isStampConsumptionRequired: z.boolean().optional().default(false),
  /**
   * 獲得前の応募フォーム定義（#3494）。
   * prizeSelectionType=SINGLE かつ確定賞品の fulfillmentType=APPLICATION_FORM のときのみ非 null
   * （LOTTERY は獲得まで賞品が確定しないため常に null）。
   * prefill は Authorization: Bearer 付きリクエストでのみ非 null。特典一覧では常に null。
   */
  applicationForm: ApplicationFormDataSchema.nullable().optional().default(null),
})

export const RewardGetResponseSchema = z.object({
  reward: RewardDataSchema.nullable(),
})

export const RewardListResponseSchema = z.object({
  rewards: z.array(RewardDataSchema),
})

export const RewardCheckResponseSchema = z.object({
  available: z.boolean(),
  reason: z.string().nullable(),
})

/** ゲスト・認証済み双方のデータを統一して扱う表示用型 */
export type RewardOverviewData = {
  id: number
  name: string | null
  image: string | null
  /** ゲスト API の label フィールド（認証済みデータには存在しない） */
  label?: string | null
  /**
   * 認証済みユーザーの獲得状況（未認証時は undefined）。UNAPPLIED / APPLIED は詳細 API のみ返す。LOST は抽選ハズレ。ENDED / SOLD_OUT は在庫切れ。
   * UNAVAILABLE は SELECT_ONE グループの選択枠を他特典が使い切っており獲得不可な状態（#3495）。
   * 値は一覧・詳細で共有する UserRewardAcquisitionStatus から導出し、ステータス追加時の追従漏れを防ぐ。
   */
  acquisitionStatus?: UserRewardAcquisitionStatus
  /**
   * 認証済みユーザーの条件進捗（未認証時は空配列）。
   * グループ特典の場合は代表特典（先頭メンバー）の conditions を使用する。
   */
  conditions: RewardConditionProgressData[]
  /**
   * 認証済みユーザーの条件グループ（未認証時・グループ無しは空配列）。#3581 のツリー表示で使う。
   * conditions と必ず同じ特典（グループ特典なら代表特典）から取ること。
   * 別の特典のものと混ぜると conditions[].groupId の対応が壊れる。
   */
  conditionGroups: RewardConditionGroupProgressData[]
}

export type StampInRewardData = z.infer<typeof StampInRewardDataSchema>
export type RewardData = z.infer<typeof RewardDataSchema>
export type RewardGetResponse = z.infer<typeof RewardGetResponseSchema>
export type RewardListResponse = z.infer<typeof RewardListResponseSchema>
export type RewardCheckResponse = z.infer<typeof RewardCheckResponseSchema>

/**
 * ゲスト API（RewardData）と認証済みユーザー API（UserRewardStatusData）をマージした型。
 * - ゲスト API が持つ label / stamps / url などリッチなフィールドをベースとする。
 * - 認証済み時のみ acquisitionStatus / conditions が設定される。
 */
export type MergedRewardData = RewardData & {
  /**
   * 認証済み時の獲得状況。未認証時は null。UNAPPLIED / APPLIED は詳細 API のみ返す。LOST は抽選ハズレ。ENDED / SOLD_OUT は在庫切れ。
   * UNAVAILABLE は SELECT_ONE グループの選択枠を他特典が使い切っており獲得不可な状態（#3495）。
   * 値は一覧・詳細で共有する UserRewardAcquisitionStatus から導出し、ステータス追加時の追従漏れを防ぐ。
   * 一覧（UserRewardStatusData）由来・詳細（UserRewardStatusDetailData）由来のどちらの status も代入できる。
   */
  acquisitionStatus: UserRewardAcquisitionStatus | null
  /** 認証済み時の獲得条件進捗。未認証時は空配列。 */
  conditions: RewardConditionProgressData[]
  /** 認証済み時の獲得条件グループ。未認証時・グループ無しは空配列。#3581 のツリー表示で使う。 */
  conditionGroups: RewardConditionGroupProgressData[]
}
